import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { CaseStudyArticle } from "@/components/resources/CaseStudyArticle";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import body from "../../content/resources/llantas-cavazos-shopify-migration-case-study.en.md?raw";

const PATH = "/resources/llantas-cavazos-migration-case-study";
const ALT = "/es/recursos/caso-llantas-cavazos-migracion";
const TITLE = "Llantas Cavazos: Migrating 8,000+ Tire SKUs to Shopify Without Disrupting the Business";
const META_TITLE = "Llantas Cavazos Shopify Migration Case Study · Shugert";
const DESCRIPTION = "How Llantas Cavazos moved 8,000+ tire SKUs to Shopify with size search, inventory sync, SEO redirects and more than 90% organic traffic retention.";
const PUBLISHED = "2026-07-30T14:53:00.000Z";
const MODIFIED = "2026-07-30T14:53:00.000Z";

const SOURCES = [
  { label: "About Llantas Cavazos", publisher: "Llantas Cavazos", href: "https://llantascavazos.com/pages/nosotros" },
  { label: "Llantas Cavazos locations", publisher: "Llantas Cavazos", href: "https://llantascavazos.com/pages/sucursales" },
  { label: "Migrate to Shopify", publisher: "Shopify Help Center", href: "https://help.shopify.com/en/manual/migrating-to-shopify" },
  { label: "Managing inventory", publisher: "Shopify Help Center", href: "https://help.shopify.com/en/manual/products/inventory" },
  { label: "Site moves with URL changes", publisher: "Google Search Central", href: "https://developers.google.com/search/docs/crawling-indexing/site-move-with-url-changes" },
  { label: "Product data specification", publisher: "Google Merchant Center Help", href: "https://support.google.com/merchants/answer/15624859" },
  { label: "Cloudflare Workers overview", publisher: "Cloudflare Developers", href: "https://developers.cloudflare.com/workers/" },
];

const RELATED = [
  { label: "Shopify migration timeline for established brands", href: "/resources/shopify-migration-timeline" },
  { label: "301 redirects for large Shopify catalogs", href: "/resources/shopify-301-redirects-large-catalogs" },
  { label: "Browse all Shopify migration resources", href: "/resources/migration" },
  { label: "Shopify migration service", href: "/shopify-migration" },
];

export const Route = createFileRoute("/resources/llantas-cavazos-migration-case-study")({
  loader: async () => {
    await setEdgeHtmlCache();
  },
  head: () =>
    buildHead({
      locale: "en",
      title: META_TITLE,
      description: DESCRIPTION,
      path: PATH,
      altPath: ALT,
      type: "article",
      author: "Samuel Noriega",
      articleAuthor: "Samuel Noriega",
      articleSection: "Migration case study",
      publishedTime: PUBLISHED,
      modifiedTime: MODIFIED,
      jsonLd: [
        articleJsonLdForPillar({
          title: TITLE,
          description: DESCRIPTION,
          path: PATH,
          publishedTime: PUBLISHED,
          modifiedTime: MODIFIED,
          section: "Migration case study",
        }),
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "Migration", path: "/resources/migration" },
          { name: "Llantas Cavazos migration case study", path: PATH },
        ]),
      ],
    }),
  component: () => (
    <SiteShell>
      <CaseStudyArticle
        locale="en"
        path={PATH}
        title={TITLE}
        excerpt="How an established Monterrey tire retailer migrated more than 8,000 SKUs to Shopify, added tire-size search and inventory synchronization, and retained more than 90% of organic traffic during the first week."
        body={body}
        updated="July 2026"
        publishedAt={PUBLISHED}
        modifiedAt={MODIFIED}
        readingMinutes={17}
        focus="How Llantas Cavazos redesigned product data, tire-size discovery, inventory synchronization, SEO continuity, customer history, and launch operations as one Shopify migration."
        audience="Ecommerce leaders, distributors, automotive retailers, and operators planning a migration with thousands of specification-driven products and existing organic demand."
        sources={SOURCES}
        related={RELATED}
      />
    </SiteShell>
  ),
});
